import { uniq } from 'lodash-es';
import { useMemo } from 'react';
import { useSecuritiesContext } from '../../contexts';
import { useUnifiedLiquidityAvailableSymbols, type UnifiedLiquidityParameter } from '../../hooks';
import { ProductTypeEnum, type Security } from '../../types';
import { isCFD, isCross, isPerpetualSwap } from '../../utils';
import { HStack, VStack } from '../Core';
import { Divider } from '../Divider';
import { Text } from '../Text';

interface UnifiedLiquidityTooltipProps {
  security: Security | undefined;
  symbols?: string[] | undefined;
  availableSymbols?: string[];
  // Descriptive tooltip props
  markets?: string[];
  marketAccounts?: string[];
  unifiedLiquidityParameter?: UnifiedLiquidityParameter;
  showDescription?: boolean;
}

/**
 * Unified liquidity tooltip with symbols grouped by product type (PERP/SWAP).
 * Can optionally show a description and use available symbols from unified liquidity parameters.
 */
export const UnifiedLiquidityTooltip = ({
  security,
  symbols,
  availableSymbols,
  markets,
  marketAccounts,
  unifiedLiquidityParameter,
  showDescription = false,
}: UnifiedLiquidityTooltipProps) => {
  // Use available symbols from unified liquidity if provided, otherwise use passed symbols
  const unifiedLiquidityAvailableSymbols = useUnifiedLiquidityAvailableSymbols({
    markets: markets ?? [],
    marketAccounts: marketAccounts ?? [],
    symbol: security?.Symbol,
  });

  const symbolsToUse = availableSymbols ?? (showDescription ? unifiedLiquidityAvailableSymbols : symbols);
  const productTypeSymbols = useGroupedSymbols(security, symbolsToUse);

  // If showing description and no symbols available, don't render
  if (showDescription && symbolsToUse?.length === 0) {
    return null;
  }

  return (
    symbolsToUse && (
      <VStack alignItems="flex-start" gap="spacing4" w="240px" data-testid="unified-liquidity-tooltip">
        {/* Header Row */}
        <HStack justifyContent="space-between" alignItems="center" w="100%">
          <Text color="colorTextSubtle" size="fontSizeSm">
            Unified Liquidity
          </Text>
        </HStack>
        <Divider />

        {/* Description section for descriptive tooltip */}
        {showDescription && unifiedLiquidityParameter && (
          <>
            <Text color="colorTextSubtle" size="fontSizeSm" fontWeight="fontWeightMedium">
              {unifiedLiquidityParameter.Description}
            </Text>
            <Divider />
          </>
        )}

        <SymbolsGroupedByProductType groups={productTypeSymbols} />
      </VStack>
    )
  );
};

interface SymbolGroup {
  title?: string;
  symbols: string[];
}

const SymbolsGroupedByProductType = ({ groups }: { groups: SymbolGroup[] }) => {
  const hasMultipleGroups = groups.length > 1;

  return (
    <VStack alignItems="flex-start" gap="spacing4">
      {groups.map((group, index) =>
        group.symbols.length === 0 ? null : (
          <VStack key={group.title || `group-${index}`} alignItems="flex-start" gap="spacing4">
            {/* only show title if there are multiple groups */}
            {hasMultipleGroups && group.title && (
              <Text color="colorTextSubtle" size="fontSizeSm" fontWeight="fontWeightMedium">
                {group.title}
              </Text>
            )}
            <VStack alignItems="flex-start" gap="spacing2">
              {group.symbols.map(symbol => (
                <Text key={symbol} color="colorTextImportant" size="fontSizeSm">
                  {symbol}
                </Text>
              ))}
            </VStack>
          </VStack>
        )
      )}
    </VStack>
  );
};

/**
 * Hook to get symbols grouped by product type.
 */
function useGroupedSymbols(
  security: Security | undefined,
  symbols: string[] | undefined,
  availableSymbols?: string[]
): SymbolGroup[] {
  const { securitiesBySymbol } = useSecuritiesContext();

  return useMemo(() => {
    // We only support UL for SPOT, PERP, SWAP and CROSS products
    if (!isPerpetualSwap(security) && !isCFD(security) && !isCross(security)) {
      // SPOT unified liquidity --> example USD, USDC, USDT, etc.
      return [{ symbols: symbols?.toSorted(caseInsensitiveSort) ?? [] }];
    }

    const symbolsToProcess = availableSymbols ?? symbols ?? [];

    const groups: SymbolGroup[] = [];

    // Group symbols by product type
    const perpSymbols: string[] = [];
    const cfdSymbols: string[] = [];
    const crossSymbols: string[] = [];

    symbolsToProcess.forEach(symbol => {
      const security = securitiesBySymbol.get(symbol);
      const displaySymbol = security?.DisplaySymbol;

      if (!displaySymbol) {
        return;
      }

      if (security.ProductType === ProductTypeEnum.PerpetualSwap) {
        perpSymbols.push(displaySymbol);
      } else if (security.ProductType === ProductTypeEnum.CFD) {
        cfdSymbols.push(displaySymbol);
      } else {
        // CROSS unified liquidity --> example BTC/USD and BTC/USD via EUR
        crossSymbols.push(displaySymbol);
      }
    });

    // Add groups only if they have symbols
    if (perpSymbols.length > 0) {
      groups.push({
        title: 'PERP',
        symbols: uniq(perpSymbols).toSorted(caseInsensitiveSort),
      });
    }
    if (cfdSymbols.length > 0) {
      groups.push({
        title: 'SWAP',
        symbols: uniq(cfdSymbols).toSorted(caseInsensitiveSort),
      });
    }
    if (crossSymbols.length > 0) {
      groups.push({ symbols: uniq(crossSymbols).toSorted(caseInsensitiveSort) });
    }

    return groups;
  }, [security, symbols, availableSymbols, securitiesBySymbol]);
}

/**
 * Case insensitive string comparison function for sorting.
 */
function caseInsensitiveSort(a: string, b: string): number {
  return a.toLowerCase().localeCompare(b.toLowerCase());
}
